/**
 * /bookingflight/results — Search Results (Phase 5).
 *
 * Orchestrates the mock search: top summary, fare calendar, quick-filter chips, advanced
 * filter sidebar, sort dropdown, and an infinite-scrolling list of FlightCards. Filtering
 * and sorting run client-side over the fetched result set. Selecting a flight stores it
 * in BookingContext and advances to passenger details.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { searchFlights, getFareCalendar, getAirports } from "../../mock/api";
import { useBooking } from "../../context/BookingContext";
import FareCalendar from "../../components/booking/FareCalendar";
import FilterChips from "../../components/booking/FilterChips";
import FilterSidebar from "../../components/booking/FilterSidebar";
import SortDropdown from "../../components/booking/SortDropdown";
import FlightCard from "../../components/booking/FlightCard";
import { LoadingSpinner, Skeleton, EmptyState, ErrorState } from "../../components/booking/states/StateViews";
import "./flight-results.css";

const PAGE_SIZE = 8;

const TRIP_LABEL = { oneway: "One Way", return: "Return", multicity: "Multi-city" };

/** Classify an ISO datetime into a time-of-day bucket. */
const timeBucket = (iso) => {
  const h = new Date(iso).getHours();
  if (h >= 5 && h < 12) return "morning";
  if (h >= 12 && h < 17) return "afternoon";
  if (h >= 17 && h < 21) return "evening";
  return "night";
};

const stopKey = (count) => (count === 0 ? "nonstop" : count === 1 ? "1stop" : "2plus");

const formatSummaryDate = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const defaultFilters = (bounds) => ({
  airlines: [],
  stops: [],
  priceMin: bounds.priceMin,
  priceMax: bounds.priceMax,
  departureBuckets: [],
  arrivalBuckets: [],
  durationMax: bounds.durationMax,
  refundable: false,
  baggageIncluded: false,
});

export default function FlightResults() {
  const navigate = useNavigate();
  const { searchCriteria, setSearchCriteria, setSelectedFlight } = useBooking();

  const [status, setStatus] = useState("loading");
  const [flights, setFlights] = useState([]);
  const [bounds, setBounds] = useState(null);
  const [filters, setFilters] = useState(null);
  const [sort, setSort] = useState("best");
  const [activeChip, setActiveChip] = useState("suggested");

  const [fareDays, setFareDays] = useState([]);
  const [fareLoading, setFareLoading] = useState(true);

  const [airportsMap, setAirportsMap] = useState({});
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const sentinelRef = useRef(null);

  const { from, to, departureDate, returnDate, tripType, cabin, directOnly } = searchCriteria || {};

  // One-time airports map for reliable summary labels.
  useEffect(() => {
    getAirports().then((list) =>
      setAirportsMap(Object.fromEntries(list.map((a) => [a.airportCode, a])))
    );
  }, []);

  // Fetch flights whenever the core search criteria change.
  const loadFlights = useCallback(() => {
    if (!searchCriteria) return;
    setStatus("loading");
    searchFlights(searchCriteria)
      .then((list) => {
        const prices = list.map((f) => f.finalPrice);
        const durations = list.map((f) => f.durationMinutes);
        const airlineNames = Array.from(new Set(list.map((f) => f.airline))).sort();
        const nextBounds = {
          airlines: airlineNames,
          priceMin: prices.length ? Math.floor(Math.min(...prices)) : 0,
          priceMax: prices.length ? Math.ceil(Math.max(...prices)) : 100000,
          durationMin: durations.length ? Math.min(...durations) : 0,
          durationMax: durations.length ? Math.max(...durations) : 1440,
        };
        setFlights(list);
        setBounds(nextBounds);
        setFilters(defaultFilters(nextBounds));
        setVisibleCount(PAGE_SIZE);
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, departureDate, returnDate, tripType, cabin, directOnly]);

  useEffect(() => {
    loadFlights();
  }, [loadFlights]);

  // Fetch fare calendar alongside.
  useEffect(() => {
    if (!departureDate) return;
    setFareLoading(true);
    getFareCalendar({ departureDate })
      .then(setFareDays)
      .catch(() => setFareDays([]))
      .finally(() => setFareLoading(false));
  }, [departureDate]);

  // Apply filters + sort.
  const results = useMemo(() => {
    if (!filters) return [];
    let list = flights.filter((f) => {
      if (filters.airlines.length && !filters.airlines.includes(f.airline)) return false;
      if (filters.stops.length && !filters.stops.includes(stopKey(f.stopCount))) return false;
      if (f.finalPrice < filters.priceMin || f.finalPrice > filters.priceMax) return false;
      if (f.durationMinutes > filters.durationMax) return false;
      if (filters.departureBuckets.length && !filters.departureBuckets.includes(timeBucket(f.departureISO)))
        return false;
      if (filters.arrivalBuckets.length && !filters.arrivalBuckets.includes(timeBucket(f.arrivalISO)))
        return false;
      if (filters.refundable && !f.refundable) return false;
      if (filters.baggageIncluded && parseInt(f.baggageAllowance, 10) <= 0) return false;
      return true;
    });

    const byPrice = (a, b) => a.finalPrice - b.finalPrice;
    const byDuration = (a, b) => a.durationMinutes - b.durationMinutes;
    const byDep = (a, b) => new Date(a.departureISO) - new Date(b.departureISO);
    const bestScore = (f) =>
      f.finalPrice / 1000 + f.durationMinutes / 30 + f.stopCount * 40;

    const sorters = {
      best: (a, b) => bestScore(a) - bestScore(b),
      cheapest: byPrice,
      lowest: byPrice,
      highest: (a, b) => b.finalPrice - a.finalPrice,
      fastest: byDuration,
      earliest: byDep,
      latest: (a, b) => new Date(b.departureISO) - new Date(a.departureISO),
    };
    return [...list].sort(sorters[sort] || sorters.best);
  }, [flights, filters, sort]);

  // Infinite scroll.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((c) => Math.min(c + PAGE_SIZE, results.length));
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [results.length]);

  // Mobile filter drawer: lock body scroll and close on Escape.
  useEffect(() => {
    if (!mobileFiltersOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setMobileFiltersOpen(false);
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [mobileFiltersOpen]);

  const handleChip = (key) => {
    setActiveChip(key);
    setFilters((f) => {
      if (!f) return f;
      if (key === "nonstop") return { ...f, stops: ["nonstop"] };
      if (key === "1stop") return { ...f, stops: ["1stop"] };
      if (key === "2stops") return { ...f, stops: ["2plus"] };
      if (key === "suggested") return { ...f, stops: [] };
      return f;
    });
    if (key === "cheapest") setSort("cheapest");
    else if (key === "fastest") setSort("fastest");
    else if (key === "suggested") setSort("best");
  };

  const handleFareSelect = (date) => {
    if (date === departureDate) return;
    setSearchCriteria({ ...searchCriteria, departureDate: date });
  };

  const handleBook = (flight) => {
    setSelectedFlight(flight);
    navigate("/bookingflight/passenger");
  };

  const resetFilters = () => {
    if (bounds) setFilters(defaultFilters(bounds));
    setActiveChip("suggested");
    setSort("best");
  };

  if (!searchCriteria) return null; // guard handles redirect

  const fromAirport = airportsMap[from];
  const toAirport = airportsMap[to];
  const visible = results.slice(0, visibleCount);

  return (
    <section className="fr-page">
      {/* Summary bar */}
      <div className="fr-summary">
        <div className="fr-summary__inner">
          <div className="fr-summary__route">
            <span className="fr-summary__trip">{TRIP_LABEL[searchCriteria.tripType] || "One Way"}</span>
            <div className="fr-summary__cities">
              <div className="fr-summary__city">
                <span className="fr-summary__city-name">
                  {fromAirport ? fromAirport.city : from}
                </span>
                <span className="fr-summary__city-code">({from})</span>
              </div>
              <i className="fas fa-arrow-right fr-summary__arrow" aria-hidden="true" />
              <div className="fr-summary__city">
                <span className="fr-summary__city-name">
                  {toAirport ? toAirport.city : to}
                </span>
                <span className="fr-summary__city-code">({to})</span>
              </div>
            </div>
          </div>
          <div className="fr-summary__meta">
            <span><i className="fas fa-calendar-alt" aria-hidden="true" /> {formatSummaryDate(departureDate)}</span>
            <span><i className="fas fa-user-friends" aria-hidden="true" /> {searchCriteria.travellers} Traveller{searchCriteria.travellers === 1 ? "" : "s"}</span>
            <span><i className="fas fa-chair" aria-hidden="true" /> {cabin}</span>
          </div>
          <button type="button" className="fr-summary__modify" onClick={() => navigate("/bookingflight")}>
            <i className="fas fa-pen" aria-hidden="true" /> Modify
          </button>
        </div>
      </div>

      <div className="fr-body">
        {/* Fare calendar */}
        <div className="fr-fare">
          <FareCalendar
            days={fareDays}
            selectedDate={departureDate}
            onSelect={handleFareSelect}
            loading={fareLoading}
          />
        </div>

        {/* Chips + sort */}
        <div className="fr-toolbar">
          <FilterChips active={activeChip} onChange={handleChip} />
          <div className="fr-toolbar__right">
            <button
              type="button"
              className="fr-mobile-filter-btn"
              onClick={() => setMobileFiltersOpen(true)}
            >
              <i className="fas fa-sliders-h" aria-hidden="true" /> Filters
            </button>
            <SortDropdown value={sort} onChange={setSort} />
          </div>
        </div>

        <div className="fr-columns">
          {/* Sidebar */}
          <div className={`fr-sidebar ${mobileFiltersOpen ? "fr-sidebar--open" : ""}`}>
            {mobileFiltersOpen && (
              <button
                type="button"
                className="fr-sidebar__close"
                onClick={() => setMobileFiltersOpen(false)}
                aria-label="Close filters"
              >
                <i className="fas fa-times" aria-hidden="true" />
              </button>
            )}
            {filters && bounds && (
              <FilterSidebar
                filters={filters}
                onChange={setFilters}
                bounds={bounds}
                onReset={resetFilters}
              />
            )}
          </div>
          {mobileFiltersOpen && (
            <div className="fr-backdrop" onClick={() => setMobileFiltersOpen(false)} />
          )}

          {/* Results list */}
          <div className="fr-list">
            {status === "loading" && (
              <>
                <LoadingSpinner label="Searching for the best fares…" compact />
                <Skeleton count={5} variant="card" />
              </>
            )}

            {status === "error" && (
              <ErrorState
                title="Search failed"
                message="We couldn't fetch flights right now. Please try again."
                onRetry={loadFlights}
              />
            )}

            {status === "ready" && results.length === 0 && (
              <EmptyState
                icon="fas fa-plane-slash"
                title="No flights match your filters"
                message="Try relaxing a filter or picking a nearby date from the fare calendar."
                action={
                  <button type="button" className="fr-reset-cta" onClick={resetFilters}>
                    Reset filters
                  </button>
                }
              />
            )}

            {status === "ready" && results.length > 0 && (
              <>
                <div className="fr-count">
                  Showing <strong>{visible.length}</strong> of {results.length} flights
                </div>
                <div className="fr-cards">
                  {visible.map((f) => (
                    <FlightCard key={f.id} flight={f} onBook={handleBook} />
                  ))}
                </div>
                {visibleCount < results.length && (
                  <div ref={sentinelRef} className="fr-sentinel">
                    <span className="bk-spinner" aria-hidden="true" /> Loading more…
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
